import { useEffect, useState } from 'react';
import Documentation from '@/components/Documentation';
import DocAccessValidation from '@/components/DocAccessValidation';
import MermaidDebugger from '@/components/MermaidDebugger';
import '@/styles/documentation.css';
import '@/styles/documentation-dark.css';

// Versão mais recente do Mermaid que suporta todos os tipos de diagramas
const MERMAID_VERSION = '10.8.0';
const MARKDOWN_IT_VERSION = '13.0.1';
const HIGHLIGHT_JS_VERSION = '11.8.0';

const DocsPage = () => {
  const [isScriptsLoaded, setIsScriptsLoaded] = useState({
    markdown: false,
    mermaid: false,
    highlight: false
  });

  // Efeito para carregar todas as bibliotecas necessárias
  useEffect(() => {
    const loadScripts = async () => {
      // Função auxiliar para carregar um script
      const loadScript = (src: string, id: string): Promise<void> => {
        return new Promise((resolve, reject) => {
          // Verificar se o script já existe
          if (document.getElementById(id)) {
            setIsScriptsLoaded(prev => ({ ...prev, [id]: true }));
            resolve();
            return;
          }

          const script = document.createElement('script');
          script.id = id;
          script.src = src;
          script.async = true;
          script.onload = () => {
            setIsScriptsLoaded(prev => ({ ...prev, [id]: true }));
            resolve();
          };
          script.onerror = (error) => {

            reject(error);
          };
          document.body.appendChild(script);
        });
      };

      // Função auxiliar para carregar um CSS
      const loadCss = (href: string, id: string): Promise<void> => {
        return new Promise((resolve) => {
          // Verificar se o CSS já existe
          if (document.getElementById(id)) {
            resolve();
            return;
          }

          const link = document.createElement('link');
          link.id = id;
          link.rel = 'stylesheet';
          link.href = href;
          link.onload = () => {
            resolve();
          };
          document.head.appendChild(link);
        });
      };

      try {
        // Carregar markdown-it
        await loadScript(`https://cdn.jsdelivr.net/npm/markdown-it@${MARKDOWN_IT_VERSION}/dist/markdown-it.min.js`, 'markdown');
        
        // Carregar Mermaid - usando a versão mais recente com suporte completo a diferentes tipos de diagramas
        await loadScript(`https://cdn.jsdelivr.net/npm/mermaid@${MERMAID_VERSION}/dist/mermaid.min.js`, 'mermaid');
        
        // Inicializar Mermaid
        if (window.mermaid) {
          window.mermaid.initialize({ 
            startOnLoad: false, // Vamos controlar manualmente quando renderizar
            theme: document.documentElement.classList.contains('dark') ? 'dark' : 'default',
            securityLevel: 'loose', // Necessário para alguns diagramas complexos
            fontFamily: 'var(--font-sans)',
            fontSize: 14,
            flowchart: {
              htmlLabels: true,
              curve: 'linear',
              useMaxWidth: false, // Importante para diagramas grandes
              defaultRenderer: 'dagre-d3' // Renderizador mais consistente
            },
            sequence: {
              diagramMarginX: 50,
              diagramMarginY: 10,
              actorMargin: 50,
              width: 150,
              height: 65,
              boxMargin: 10,
              boxTextMargin: 5,
              noteMargin: 10,
              messageMargin: 35,
              mirrorActors: false,
              bottomMarginAdj: 10,
              useMaxWidth: false
            },
            er: {
              diagramPadding: 20,
              layoutDirection: 'TB',
              minEntityWidth: 100,
              minEntityHeight: 75,
              entityPadding: 15,
              useMaxWidth: false
            },
            gantt: {
              titleTopMargin: 25,
              barHeight: 20,
              barGap: 4,
              topPadding: 50,
              leftPadding: 75,
              gridLineStartPadding: 35,
              fontSize: 11,
              fontFamily: 'var(--font-sans)',
              useMaxWidth: false
            },
            pie: {
              useMaxWidth: false
            },
            // Estas configurações ajudam com problemas de renderização
            logLevel: 'error', // Mostra apenas erros críticos
            deterministicIds: true, // Ajuda com estabilidade de renderização
            maxTextSize: 100000, // Permite diagramas maiores
            defaultRenderer: 'dagre-wrapper' // Mais compatível com diferentes tipos
          });
          
          // Adiciona um listener para re-renderizar diagramas em mudanças de tema
          window.addEventListener('themeChange', () => {
            if (window.mermaid) {
              try {
                window.mermaid.initialize({
                  theme: document.documentElement.classList.contains('dark') ? 'dark' : 'default'
                });
                window.mermaid.run();
              } catch (e) {

              }
            }
          });
        }

        // Carregar Highlight.js (CSS e script) - Agora de forma local
        await loadCss(`/js/github.min.css`, 'highlight-css');
        await loadScript(`/js/highlight.min.js`, 'highlight');
        
        // Inicializar Highlight.js
        if (window.hljs) {
          window.hljs.configure({
            ignoreUnescapedHTML: true,
            languages: [
              'javascript', 'typescript', 'jsx', 'tsx', 'html', 'css', 
              'bash', 'json', 'yaml', 'markdown', 'sql', 'python'
            ]
          });
        }

        // Carregar plugin adicional para Markdown-it
        await loadScript('https://cdn.jsdelivr.net/npm/markdown-it-container@3.0.0/dist/markdown-it-container.min.js', 'markdown-container');

      } catch (error) {

      }
    };

    loadScripts();

    // Limpeza ao desmontar o componente
    return () => {
      // Remover scripts apenas se não houver outras páginas que possam precisar deles
      ['markdown', 'mermaid', 'highlight'].forEach(id => {
        const script = document.getElementById(id);
        if (script) {
          document.body.removeChild(script);
        }
      });

      // Remover CSS
      const css = document.getElementById('highlight-css');
      if (css) {
        document.head.removeChild(css);
      }
    };
  }, []);

  return (
    <DocAccessValidation>
      <Documentation />
      <MermaidDebugger />
    </DocAccessValidation>
  );
};

export default DocsPage;
